import { apiClient } from 'utils/ApiClient';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';
import { API_BASE_URL } from 'envConfig';
import { Logger } from 'utils/Logger';
import { 
    Asset, 
    Folder, 
    AssetsResponse, 
    FolderTreeResponse,
    UploadAssetPayload,
    CreateFolderPayload,
    SortField,
    SortOrder,
    StorageUsage,
    FolderPropertiesResponse,
    SaveFolderPropertiesPayload,
    AssetPropertiesResponse,
} from './AssetExplorer.types';

const LOG_PREFIX = 'AssetExplorer.api';

/**
 * Default API route prefix for the Asset Management module (`/ae/...` on st-ck-server).
 * Beam on Screen mounts the same explorer against `/bos/...`.
 */
export const DEFAULT_ASSET_EXPLORER_API_BASE_PATH = '/ae';

const normalizeBasePath = (basePath: string | undefined): string => {
    const raw = (basePath || DEFAULT_ASSET_EXPLORER_API_BASE_PATH).trim();
    const withLeading = raw.startsWith('/') ? raw : `/${raw}`;
    return withLeading.replace(/\/+$/, '') || DEFAULT_ASSET_EXPLORER_API_BASE_PATH;
};

const absoluteApiUrl = (path: string): string => {
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    return `${baseUrl}${path}`;
};

/** Bearer + active company headers for raw `fetch` (binary endpoints, no JSON content type). */
const binaryFetchHeaders = (): Record<string, string> => {
    const authHeaders = LocalStorageAuth.getAuthHeaders();
    const { 'Content-Type': _omit, ...headers } = authHeaders as Record<string, string>;
    void _omit;
    const activeCompanyId = LocalStorageAuth.getActiveCompanyId();
    if (activeCompanyId) headers['X-Active-Company-Id'] = activeCompanyId;
    return headers;
};

const blobToDataUrl = (blob: Blob): Promise<string | null> =>
    new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result;
            resolve(typeof result === 'string' ? result : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
    });

/**
 * Builds the explorer REST client for a given route prefix.
 *
 * `/ae` → Asset Management (`assetsmanagement.ai`), `/bos` → Beam on Screen (`beamonscreen.com`).
 * Both prefixes expose the same contract; the server decides which storage they resolve to.
 */
export const createAssetExplorerApi = (basePath?: string) => {
    const base = normalizeBasePath(basePath);
    const folderThumbnailDataUrlCache = new Map<string, string>();

    // ============================================
    // Folder API Functions
    // ============================================

    /** Get or create root folder for the company */
    const getRootFolder = async (): Promise<Folder> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting root folder`);
            const response = await apiClient.get<Folder>(`${base}/folders/root`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting root folder: ${error}`);
            throw error;
        }
    };

    /** Get folder tree (all folders as hierarchical structure) */
    const getFolderTree = async (): Promise<FolderTreeResponse[]> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting folder tree`);
            const response = await apiClient.get<FolderTreeResponse[]>(`${base}/folders/tree`);
            return response.data || [];
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting folder tree: ${error}`);
            throw error;
        }
    };

    /** Immediate children of a folder (flat list; tree children may be empty until tree reload). */
    const getChildFolders = async (parentId: string): Promise<FolderTreeResponse[]> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting child folders for parent: ${parentId}`);
            const response = await apiClient.get<FolderTreeResponse[]>(`${base}/folders/${parentId}/children`);
            const list = response.data || [];
            return list.map((f) => ({
                ...f,
                children: Array.isArray(f.children) ? f.children : [],
            }));
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting child folders for ${parentId}: ${error}`);
            throw error;
        }
    };

    /** Get folder by ID */
    const getFolderById = async (folderId: string): Promise<Folder> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting folder: ${folderId}`);
            const response = await apiClient.get<Folder>(`${base}/folders/${folderId}`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting folder ${folderId}: ${error}`);
            throw error;
        }
    };

    /** Create a new folder */
    const createFolder = async (payload: CreateFolderPayload): Promise<Folder> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Creating folder: ${JSON.stringify(payload)}`);
            const response = await apiClient.post<Folder>(`${base}/folders`, payload);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error creating folder: ${error}`);
            throw error;
        }
    };

    /** Update folder (rename, metadata) */
    const updateFolder = async (folderId: string, data: { name?: string; metadata?: any }): Promise<Folder> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Updating folder ${folderId}: ${JSON.stringify(data)}`);
            const response = await apiClient.put<Folder>(`${base}/folders/${folderId}`, data);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error updating folder ${folderId}: ${error}`);
            throw error;
        }
    };

    const getFolderProperties = async (folderId: string): Promise<FolderPropertiesResponse> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting folder properties: ${folderId}`);
            const response = await apiClient.get<FolderPropertiesResponse>(`${base}/folders/${folderId}/properties`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting folder properties ${folderId}: ${error}`);
            throw error;
        }
    };

    const saveFolderProperties = async (
        folderId: string,
        payload: SaveFolderPropertiesPayload,
    ): Promise<FolderPropertiesResponse> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Saving folder properties: ${folderId}`);
            const response = await apiClient.put<FolderPropertiesResponse>(
                `${base}/folders/${folderId}/properties`,
                payload,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error saving folder properties ${folderId}: ${error}`);
            throw error;
        }
    };

    const getAssetProperties = async (assetId: string): Promise<AssetPropertiesResponse> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting asset properties: ${assetId}`);
            const response = await apiClient.get<AssetPropertiesResponse>(`${base}/assets/${assetId}/properties`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting asset properties ${assetId}: ${error}`);
            throw error;
        }
    };

    const saveAssetProperties = async (
        assetId: string,
        customFields: Array<{ fieldKey: string; value?: unknown }>,
    ): Promise<AssetPropertiesResponse> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Saving asset properties: ${assetId}`);
            const response = await apiClient.put<AssetPropertiesResponse>(`${base}/assets/${assetId}/properties`, {
                customFields,
            });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error saving asset properties ${assetId}: ${error}`);
            throw error;
        }
    };

    const saveAssetPropertiesBulk = async (
        assetIds: string[],
        customFields: Array<{ fieldKey: string; value?: unknown }>,
        mergeMode: 'overwrite' | 'append',
    ): Promise<{ updatedCount: number }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Saving bulk asset properties: ${assetIds.length} (${mergeMode})`);
            const response = await apiClient.put<{ updatedCount: number }>(`${base}/assets/properties`, {
                assetIds,
                customFields,
                mergeMode,
            });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error saving bulk asset properties: ${error}`);
            throw error;
        }
    };

    /**
     * Authenticated binary fetch of a folder thumbnail as a data URL.
     * A plain <img src> would not send the bearer token.
     */
    const fetchFolderThumbnailAsDataUrl = async (
        folderId: string,
        version?: string | number | null,
    ): Promise<string | null> => {
        const cacheKey = `${folderId}:${version || ''}`;
        const cached = folderThumbnailDataUrlCache.get(cacheKey);
        if (cached) return cached;

        const endpoint = absoluteApiUrl(`${base}/folders/${encodeURIComponent(folderId)}/thumbnail`);

        try {
            const response = await fetch(endpoint, { method: 'GET', headers: binaryFetchHeaders() });
            if (!response.ok) {
                Logger.warn(LOG_PREFIX, `fetchFolderThumbnailAsDataUrl: ${response.status} for ${folderId}`);
                return null;
            }
            const dataUrl = await blobToDataUrl(await response.blob());
            if (dataUrl) {
                folderThumbnailDataUrlCache.set(cacheKey, dataUrl);
            }
            return dataUrl;
        } catch (err) {
            Logger.warn(LOG_PREFIX, `fetchFolderThumbnailAsDataUrl: ${err}`);
            return null;
        }
    };

    /** Delete folder and all its contents */
    const deleteFolder = async (
        folderId: string,
    ): Promise<{ message: string; deletedFolders: number; deletedAssets: number }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Deleting folder: ${folderId}`);
            const response = await apiClient.delete<{ message: string; deletedFolders: number; deletedAssets: number }>(
                `${base}/folders/${folderId}`,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error deleting folder ${folderId}: ${error}`);
            throw error;
        }
    };

    // ============================================
    // Asset API Functions
    // ============================================

    /**
     * Get assets in a folder with pagination.
     *
     * When `publishedOnly` is true, the server filters out assets that haven't
     * been published yet (Email Image Block "Select Published Assets" picker).
     */
    const getAssets = async (
        folderId?: string,
        page: number = 0,
        limit: number = 20,
        sortBy: SortField = 'createdAt',
        sortOrder: SortOrder = 'desc',
        publishedOnly: boolean = false,
    ): Promise<AssetsResponse> => {
        try {
            const qs = `page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}${
                publishedOnly ? '&publishedOnly=true' : ''
            }`;
            const endpoint = folderId ? `${base}/assets/folder/${folderId}?${qs}` : `${base}/assets?${qs}`;

            Logger.debug(LOG_PREFIX, `Getting assets: ${endpoint}`);
            const response = await apiClient.get<AssetsResponse>(endpoint);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting assets: ${error}`);
            throw error;
        }
    };

    /** Company-wide used storage vs the active module plan quota. */
    const getStorageUsage = async (): Promise<StorageUsage> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting storage usage`);
            const response = await apiClient.get<StorageUsage>(`${base}/assets/storage-usage`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting storage usage: ${error}`);
            throw error;
        }
    };

    /** Get single asset by ID */
    const getAssetById = async (assetId: string): Promise<Asset> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting asset: ${assetId}`);
            const response = await apiClient.get<Asset>(`${base}/assets/${assetId}`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting asset ${assetId}: ${error}`);
            throw error;
        }
    };

    /** Upload asset */
    const uploadAsset = async (payload: UploadAssetPayload): Promise<Asset> => {
        try {
            Logger.debug(
                LOG_PREFIX,
                `[${base}] Uploading asset: ${JSON.stringify({ ...payload, assetData: '[BASE64_DATA]' })}`,
            );
            const response = await apiClient.post<Asset>(`${base}/assets`, payload);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error uploading asset: ${error}`);
            throw error;
        }
    };

    /** Update asset metadata */
    const updateAsset = async (assetId: string, data: { name?: string; metadata?: any }): Promise<Asset> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Updating asset ${assetId}: ${JSON.stringify(data)}`);
            const response = await apiClient.put<Asset>(`${base}/assets/${assetId}`, data);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error updating asset ${assetId}: ${error}`);
            throw error;
        }
    };

    /** Delete single asset */
    const deleteAsset = async (assetId: string): Promise<{ message: string }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Deleting asset: ${assetId}`);
            const response = await apiClient.delete<{ message: string }>(`${base}/assets/${assetId}`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error deleting asset ${assetId}: ${error}`);
            throw error;
        }
    };

    /** Bulk delete assets */
    const bulkDeleteAssets = async (assetIds: string[]): Promise<{ message: string; deletedCount: number }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Bulk deleting assets: ${assetIds.length} items`);
            const response = await apiClient.post<{ message: string; deletedCount: number }>(
                `${base}/assets/bulk-delete`,
                { assetIds },
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error bulk deleting assets: ${error}`);
            throw error;
        }
    };

    const moveExplorerItems = async (payload: {
        destinationFolderId: string;
        assetIds: string[];
        folderIds: string[];
    }): Promise<{ message: string; movedFolderRoots: number; movedAssets: number }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Move explorer items: ${JSON.stringify({ ...payload })}`);
            const response = await apiClient.post<{ message: string; movedFolderRoots: number; movedAssets: number }>(
                `${base}/explorer/move`,
                payload,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error moving explorer items: ${error}`);
            throw error;
        }
    };

    const publishExplorerItems = async (payload: {
        assetIds: string[];
        folderIds: string[];
    }): Promise<{ message: string; publishedCount: number }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Publish explorer items: ${JSON.stringify(payload)}`);
            const response = await apiClient.post<{ message: string; publishedCount: number }>(
                `${base}/explorer/publish`,
                payload,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error publishing explorer items: ${error}`);
            throw error;
        }
    };

    const shareExplorerFolders = async (folderIds: string[]): Promise<{ message: string; sharedCount: number }> => {
        try {
            const response = await apiClient.post<{ message: string; sharedCount: number }>(`${base}/explorer/share`, { folderIds });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error sharing folders: ${error}`);
            throw error;
        }
    };

    const unshareExplorerFolders = async (folderIds: string[]): Promise<{ message: string; unsharedCount: number }> => {
        try {
            const response = await apiClient.post<{ message: string; unsharedCount: number }>(`${base}/explorer/unshare`, { folderIds });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error unsharing folders: ${error}`);
            throw error;
        }
    };

    const unpublishExplorerItems = async (payload: {
        assetIds: string[];
        folderIds: string[];
    }): Promise<{ message: string; unpublishedCount: number }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Unpublish explorer items: ${JSON.stringify(payload)}`);
            const response = await apiClient.post<{ message: string; unpublishedCount: number }>(
                `${base}/explorer/unpublish`,
                payload,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error unpublishing explorer items: ${error}`);
            throw error;
        }
    };

    /**
     * Search assets across all folders owned by the caller.
     * Pass `publishedOnly=true` to restrict results to published assets only.
     */
    const searchAssets = async (query: string, publishedOnly: boolean = false): Promise<Asset[]> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Searching assets: ${query} (publishedOnly=${publishedOnly})`);
            const endpoint = `${base}/assets/search?query=${encodeURIComponent(query)}${
                publishedOnly ? '&publishedOnly=true' : ''
            }`;
            const response = await apiClient.get<Asset[]>(endpoint);
            return response.data || [];
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error searching assets: ${error}`);
            throw error;
        }
    };

    /**
     * Fetches a published S3 asset through the API gateway proxy and returns it
     * as a `data:` URL the browser can render without any CORS round-trip.
     *
     * Used by `ThumbnailGenerator` to inline CDN-hosted images before invoking
     * html2canvas so the resulting canvas is never tainted, even when the S3
     * bucket / CloudFront CORS configuration is missing or stale.
     *
     * Returns `null` if the asset can't be fetched — callers should fall back
     * to the original URL in that case.
     */
    const fetchPublishedAssetAsDataUrl = async (cdnUrl: string): Promise<string | null> => {
        if (!cdnUrl || typeof cdnUrl !== 'string') return null;

        const endpoint = absoluteApiUrl(`${base}/assets/published-proxy?url=${encodeURIComponent(cdnUrl)}`);

        try {
            const response = await fetch(endpoint, { method: 'GET', headers: binaryFetchHeaders() });
            if (!response.ok) {
                Logger.warn(
                    LOG_PREFIX,
                    `fetchPublishedAssetAsDataUrl: proxy returned ${response.status} for ${cdnUrl}`,
                );
                return null;
            }
            return await blobToDataUrl(await response.blob());
        } catch (err) {
            Logger.warn(LOG_PREFIX, `fetchPublishedAssetAsDataUrl: ${err}`);
            return null;
        }
    };

    /**
     * Folder IDs (within the caller's explorer scope) that currently contain at
     * least one published asset. Used to grey out empty folders in pickers.
     */
    const getFoldersWithPublishedAssets = async (): Promise<string[]> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting folders containing published assets`);
            const response = await apiClient.get<{ folderIds: string[] }>(`${base}/assets/published-folders`);
            return response.data?.folderIds || [];
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting folders with published assets: ${error}`);
            throw error;
        }
    };

    /** Get download URL for asset */
    const getDownloadUrl = async (assetId: string): Promise<{ downloadUrl: string; filename: string }> => {
        try {
            Logger.debug(LOG_PREFIX, `[${base}] Getting download URL for asset: ${assetId}`);
            const response = await apiClient.get<{ downloadUrl: string; filename: string }>(
                `${base}/assets/${assetId}/download`,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `Error getting download URL for asset ${assetId}: ${error}`);
            throw error;
        }
    };

    return {
        basePath: base,
        getRootFolder,
        getFolderTree,
        getChildFolders,
        getFolderById,
        createFolder,
        updateFolder,
        getFolderProperties,
        saveFolderProperties,
        getAssetProperties,
        saveAssetProperties,
        saveAssetPropertiesBulk,
        fetchFolderThumbnailAsDataUrl,
        deleteFolder,
        getAssets,
        getStorageUsage,
        getAssetById,
        uploadAsset,
        updateAsset,
        deleteAsset,
        bulkDeleteAssets,
        moveExplorerItems,
        publishExplorerItems,
        unpublishExplorerItems,
        shareExplorerFolders,
        unshareExplorerFolders,
        searchAssets,
        fetchPublishedAssetAsDataUrl,
        getFoldersWithPublishedAssets,
        getDownloadUrl,
    };
};

export type AssetExplorerApi = ReturnType<typeof createAssetExplorerApi>;

/**
 * Default client bound to the Asset Management routes (`/ae`).
 * Kept as named exports so existing callers (Email builder picker, ThumbnailGenerator, …) keep working.
 */
export const defaultAssetExplorerApi = createAssetExplorerApi(DEFAULT_ASSET_EXPLORER_API_BASE_PATH);

export const {
    getRootFolder,
    getFolderTree,
    getChildFolders,
    getFolderById,
    createFolder,
    updateFolder,
    getFolderProperties,
    saveFolderProperties,
    getAssetProperties,
    saveAssetProperties,
    saveAssetPropertiesBulk,
    fetchFolderThumbnailAsDataUrl,
    deleteFolder,
    getAssets,
    getStorageUsage,
    getAssetById,
    uploadAsset,
    updateAsset,
    deleteAsset,
    bulkDeleteAssets,
    moveExplorerItems,
    publishExplorerItems,
    unpublishExplorerItems,
    searchAssets,
    fetchPublishedAssetAsDataUrl,
    getFoldersWithPublishedAssets,
    getDownloadUrl,
} = defaultAssetExplorerApi;

// ============================================
// Helper Functions
// ============================================

/**
 * Convert file to base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            // Remove the data:mime;base64, prefix
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = (error) => reject(error);
    });
};

const MAX_FOLDER_THUMBNAIL_BYTES = 1024 * 1024;
const FOLDER_THUMBNAIL_MAX_PX = 512;

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not read image'));
        img.src = src;
    });

const canvasToJpegBase64 = (canvas: HTMLCanvasElement, quality: number): Promise<string> =>
    new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Could not encode thumbnail'));
                    return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    resolve(result.split(',')[1]);
                };
                reader.onerror = () => reject(new Error('Could not encode thumbnail'));
                reader.readAsDataURL(blob);
            },
            'image/jpeg',
            quality,
        );
    });

/**
 * Downscale an image file to ~512px JPEG so folder thumbnails stay under the 1 MB cap.
 */
export const downscaleImageForFolderThumbnail = async (
    file: File,
): Promise<{ base64: string; contentType: string }> => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const img = await loadImageElement(objectUrl);
        const maxDim = Math.max(img.width || 1, img.height || 1);
        const scale = Math.min(1, FOLDER_THUMBNAIL_MAX_PX / maxDim);
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not process thumbnail');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.85;
        let base64 = await canvasToJpegBase64(canvas, quality);
        while (Math.ceil((base64.length * 3) / 4) > MAX_FOLDER_THUMBNAIL_BYTES && quality > 0.4) {
            quality -= 0.15;
            base64 = await canvasToJpegBase64(canvas, quality);
        }
        if (Math.ceil((base64.length * 3) / 4) > MAX_FOLDER_THUMBNAIL_BYTES) {
            throw new Error('Thumbnail must be 1 MB or smaller after resize');
        }
        return { base64, contentType: 'image/jpeg' };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * HEIC/HEIF are often classified as images but browsers cannot decode them in &lt;img&gt;.
 */
const IMAGE_MIME_NOT_BROWSER_PREVIEWABLE = /image\/(heic|heif)\b/i;

export function mimeTypeSupportsInlineImagePreview(mimeType: string): boolean {
    const m = (mimeType || '').trim().toLowerCase();
    if (!m.startsWith('image/')) return false;
    return !IMAGE_MIME_NOT_BROWSER_PREVIEWABLE.test(m);
}

export function fileExtensionSupportsInlineImagePreview(filename: string): boolean {
    const ext = filename.includes('.') ? filename.split('.').pop()?.toLowerCase() ?? '' : '';
    if (ext === 'heic' || ext === 'heif') return false;
    return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filename);
}

/** Local object URL preview in upload dialog */
export function fileQualifiesForLocalImagePreview(file: File): boolean {
    const t = file.type?.trim().toLowerCase() || '';
    if (t) {
        if (IMAGE_MIME_NOT_BROWSER_PREVIEWABLE.test(t)) return false;
        if (t.startsWith('image/')) return true;
        return false;
    }
    return fileExtensionSupportsInlineImagePreview(file.name);
}

/** Whether tile/carousel should render &lt;img src=s3Url&gt; for this asset */
export function assetSupportsInlineImagePreview(asset: Pick<Asset, 'type' | 'mimeType' | 'originalName' | 'name'>): boolean {
    if (asset.type !== 'IMAGE') return false;
    const m = asset.mimeType?.trim().toLowerCase() || '';
    const label = asset.originalName || asset.name || '';
    const ext = label.includes('.') ? label.split('.').pop()?.toLowerCase() ?? '' : '';
    if (ext === 'heic' || ext === 'heif') return false;
    if (m && IMAGE_MIME_NOT_BROWSER_PREVIEWABLE.test(m)) return false;
    if (m.startsWith('image/')) return true;
    return fileExtensionSupportsInlineImagePreview(label);
}

/**
 * Absolute URL for a published Explorer asset on the public CDN.
 * `assetsS3CdnUrl` comes from `/system/client/config` (`ASSETS_S3_CDN_URL` on the server).
 *
 * If `publishedRelativeUrl` begins with `/PUBLISHED` or `PUBLISHED/`, that segment is removed:
 * the CDN origin uses origin path `/PUBLISHED`, so the stored key must not repeat it in the URL path.
 */
export function isAssetPublished(asset: { publishedRelativeUrl?: string | null }): boolean {
    return Boolean(asset.publishedRelativeUrl && String(asset.publishedRelativeUrl).trim());
}

export function buildExplorerPublishedAssetUrl(
    assetsS3CdnUrl: string | null | undefined,
    publishedRelativeUrl: string | null | undefined,
): string | null {
    const base = assetsS3CdnUrl?.trim();
    const rel = publishedRelativeUrl?.trim();
    if (!base || !rel) return null;
    const normalizedBase = base.replace(/\/+$/, '');
    let path = rel.replace(/^\/+/, '');
    const pathUpper = path.toUpperCase();
    if (pathUpper.startsWith('PUBLISHED/')) {
        path = path.slice('PUBLISHED/'.length);
    } else if (pathUpper === 'PUBLISHED') {
        path = '';
    }
    path = path.replace(/^\/+/, '');
    if (!path) return null;
    return `${normalizedBase}/${path}`;
}

async function writeTextToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            /* fall through */
        }
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}

/** Copy string to clipboard (Clipboard API with execCommand fallback). */
export async function copyTextToClipboard(text: string): Promise<boolean> {
    return writeTextToClipboard(text);
}

/**
 * Get asset type icon name based on type
 */
export const getAssetTypeIcon = (type: string): string => {
    switch (type) {
        case 'IMAGE': return 'Image';
        case 'VIDEO': return 'VideoFile';
        case 'AUDIO': return 'AudioFile';
        case 'ZIP':
        case 'DOCUMENT':
        default:
            return 'InsertDriveFile';
    }
};

/**
 * Get asset type from file mime type
 */
export const getAssetTypeFromMime = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'IMAGE';
    if (mimeType.startsWith('video/')) return 'VIDEO';
    if (mimeType.startsWith('audio/')) return 'AUDIO';
    if (mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed') return 'ZIP';
    if (mimeType.startsWith('application/') || mimeType.startsWith('text/')) return 'DOCUMENT';
    return 'OTHER';
};
